import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import StatsWidgets from "../../Admin/StatsWidgets";
import { useGetClientsStats } from "../../../hooks/useGetClientsStats";
import { Button, Input, InputNumber } from "antd";
import { PlusCircleFilled } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import "../../../styles/superAdmin/addClient.css";
import {
  closeModal,
  makeAddModeFalse,
  makeEditModeFalse,
  makeViewModeFalse,
  updateCompanyDetails,
} from "../../../redux/Admin/adminActions";
import { useGetClient } from "../../../hooks/useGetClient";
import SkeletonUI from "./loaders/SkeletonUI";
import BackButton from "./BackButton";
import { MdOutlineArrowBack } from "react-icons/md";
import LoadingSpinner from "./loaders/LoadingSpinner";
import { useGetActiveModules } from "../../../hooks/useGetActiveModules";
import ErrorPopup from "./modals/ErrorModal";
import SuccessPopup from "./modals/SuccessModal";
import axios from "axios";

const ClientDetailsPage = () => {
  const { id } = useParams();
  const dispatch = useDispatch();

  const [successMessage, setSuccessMessage] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

  const editMode = useSelector((state) => state.admin.editMode);
  const editCompany = useSelector((state) => state.admin.editCompany);
  

  const { data: clientStats } = useGetClientsStats();
  const { data: client, isLoading: clientLoading, refetch: refetchClient } = useGetClient(id);
  const { data: activeModules, isLoading: activeModulesloading } =
    useGetActiveModules();

  const navigate = useNavigate();
  const handleViewDetailsClose = () => {
    dispatch(closeModal(0));
    dispatch(makeEditModeFalse());
    dispatch(makeAddModeFalse());
    dispatch(makeViewModeFalse());
  };

  const handleAddNewClientClick = () => {
    handleViewDetailsClose();
    navigate("/super_admin/add-client");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    dispatch(updateCompanyDetails(name, value));
  };

  const handleBackButtonClick = () => {
    dispatch(makeEditModeFalse());
    navigate(-1);
  };

  const handleModuleToggle = (moduleId) => {
    const moduleIdStr = String(moduleId);
    const isSubscribed = editCompany.subscribedModules?.includes(moduleIdStr);

    dispatch(
      updateCompanyDetails(
        "subscribedModules",
        isSubscribed
          ? editCompany.subscribedModules.filter((id) => id !== moduleIdStr)
          : [...editCompany.subscribedModules, moduleIdStr]
      )
    );
  };

  // 
 const handleEditClient = async (e) => {
  e.preventDefault();

  try {
    setIsLoading(true);
    const token = localStorage.getItem("superAdminToken");

    if (!token) {
      return console.error("Token not found");
    }

    // Build the module subscription details
    const allModules = activeModules?.payload || []; // assuming this contains all available modules with name and id
    const subscribedModuleIds = editCompany.subscribedModules || [];

    const modulesPayload = allModules.map((mod) => ({
      id: mod.id,
      name: mod.name,
      subscribed: subscribedModuleIds.includes(String(mod.id)),
    }));

    // Final API payload
    const payload = {
      company_details: {
        company_name: editCompany?.name,
        company_email: editCompany?.email,
        company_contact: editCompany?.contact,
        company_site: editCompany?.site,
        number_of_users: Number(editCompany?.users),
      },
      subscription_details: {
        modules: modulesPayload,
      },
    };

    const response = await axios.put(
      `${process.env.REACT_APP_BASE_URL}/super-admin/client-management/update-client/${id}`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    setSuccessMessage(response.data.message);
    
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    refetchClient()
  } catch (error) {
    setIsLoading(false);

    const status = error?.response?.status;
    const apiErrors = error?.response?.data?.errors;
    const fallbackMessage =
      error?.response?.data?.message || "Something went wrong";

    if (status >= 400 && status < 500) {
      // Show detailed error for client-side issues (400 range)
      if (apiErrors && typeof apiErrors === "object") {
        const firstErrorMessage =
          Object.values(apiErrors)[0]?.[0] || fallbackMessage;
        setErrorMessage(firstErrorMessage);
      } else {
        setErrorMessage(fallbackMessage);
      }
    } else {
      // Show generic message for 500+ errors
      setErrorMessage("An error occurred. Please try again later.");
    }

    console.error("Error:", error.response?.data || error.message);
  }
};

// 
  useEffect(() => {
    if (client) {
      dispatch({
        type: "INITIALIZE_EDIT_COMPANY_FORM",
        payload: {
          name: client.name || "",
          email: client.email || "",
          contact: client.phone || "",
          site: client.site || "",
          users: client.expected_users?.toString() || "",
          subscribedModules: client.modules?.map((m) => m.id) || [],
        },
      });
    }
  }, [client, dispatch]);

  const renderViewMode = () => {
    return (
      <>
        <div className="w-100 row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 row-cols-xl-5 g-4 justify-content-lg-start justify-content-center align-items-center">
          <StatsWidgets clientStats={clientStats} />
          <div className="add_newBtn ms-lg-5">
            <Button
              size="large"
              className="add-client-button d-flex align-items-center justify-content-center"
              onClick={handleAddNewClientClick}
              icon={<PlusCircleFilled className="me-2" />}
            >
              Add New Client
            </Button>
          </div>
        </div>

        <div className="company-details">
          <div className="details-container" style={{ padding: "1rem" }}>
            <BackButton backFn={() => navigate(-1)} />
            <h2>Company Details</h2>
            {clientLoading ? (
              <SkeletonUI />
            ) : (
              <form>
                <div className="info-flex">
                  <div className="info-section">
                    <div className="info-group">
                      <h1 className="info-label">Company Name:</h1>
                      <span className="info-value">{client?.name}</span>
                    </div>
                    <div className="info-group">
                      <h1 className="info-label">Company Site:</h1>
                      <span className="info-value">{client?.site}</span>
                    </div>
                  </div>

                  <div className="info-section">
                    <div className="info-group">
                      <h1 className="info-label">Company Email:</h1>
                      <span className="info-value">{client?.email}</span>
                    </div>
                    <div className="info-group">
                      <h1 className="info-label">Number of users:</h1>
                      <span className="info-value">{client?.total_employee}</span>
                    </div>
                  </div>

                  <div className="info-section">
                    <div className="info-group">
                      <h1 className="info-label">Company Contact:</h1>
                      <span className="info-value">
                        {client?.phone || "N/A"}
                      </span>
                    </div>
                    <div className="info-group">
                      <h1 className="info-label">Subscribed Modules:</h1>
                      <span className="info-value">
                        {client?.subscribed_modules}
                      </span>
                    </div>
                  </div>
                </div>

                <h2>Available Modules</h2>
                <div className="modules-flex">
                  {client?.modules?.map((module) => (
                    <div key={module.id} className="module-card">
                      <h4>{module.name}</h4>
                      <div className="expiry-date">
                        Expiry date: {module.expiry_date}
                      </div>
                    </div>
                  ))}
                </div>
              </form>
            )}
          </div>
        </div>
      </>
    );
  };

  const renderEditMode = () => {
    const websiteSuffix = `.${process.env.REACT_APP_CLIENT_WEBSITE_LINK || "workwavehr.sslinsight.com"}`;
    return (
      <>
        {successMessage && (
        <SuccessPopup
          message={successMessage}
          onClose={() => setSuccessMessage(null)}
        />
      )}
      {errorMessage && (
        <ErrorPopup
          message={errorMessage}
          onClose={() => setErrorMessage(null)}
        />
      )}
        <div className="action-header">
          <h1>Update Client</h1>
          <p>
            New clients/companies can be edited and updated manually by admin
          </p>
        </div>
        <div className="company-details">
          <div className="details-container" style={{ padding: "1rem" }}>
            <BackButton backFn={() => handleBackButtonClick()} />
            <h2>Company Details</h2>
            {clientLoading ? (
              <SkeletonUI />
            ) : (
              <form onSubmit={handleEditClient}>
                <div className="info-flex">
                  <div className="info-section">
                    <div className="info-group">
                      <h1 className="info-label">Company Name:</h1>
                      <Input
                        name="name"
                        value={editCompany?.name}
                        onChange={handleInputChange}
                        placeholder="Enter company name"
                        size="large"
                      />
                    </div>
                    <div className="info-group">
  <h1 className="info-label">Company Site:</h1>
  <div className="site-input-wrapper">
    <Input.Group compact>
      <Input
        name="site"
        value={
          editCompany?.site
            ? editCompany.site.replace(websiteSuffix, "")
            : ""
        }
        onChange={handleInputChange}
        placeholder="Enter site name"
        size="large"
        style={{
          width: "calc(100% - 220px)",
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          borderRight: "none",
        }}
      />
      <Input
        value={websiteSuffix}
        size="large"
        disabled
        style={{
          width: 220,
          backgroundColor: "#16166c",
          color: "white",
          fontWeight: 500,
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
        }}
      />
    </Input.Group>
  </div>
</div>

                  </div>

                  <div className="info-section">
                    <div className="info-group">
                      <h1 className="info-label">Company Email:</h1>
                      <Input
                        name="email"
                        type="email"
                        value={editCompany?.email}
                        onChange={handleInputChange}
                        placeholder="Enter company email"
                        size="large"
                      />
                    </div>
                    <div className="info-group">
                      <h1 className="info-label">Number of users:</h1>
                      <Input
                        id="number-input"
                        name="users"
                        value={editCompany?.users}
                         onChange={handleInputChange}
                        size="large"
                      />
                    </div>
                  </div>

                  <div className="info-section">
                    <div className="info-group">
                      <h1 className="info-label">Company Contact:</h1>
                      <Input
                        name="contact"
                        value={editCompany?.contact}
                        onChange={handleInputChange}
                        size="large"
                        placeholder="Enter contact number"
                      />
                    </div>
                  </div>
                </div>

                <h2>Subscription Details</h2>
                <div className="modules-flex">
                  {activeModules?.payload?.map((module) => (
                    <div
                      key={module.id}
                      className="module-card module-card-border"
                    >
                      <div className="module-checkbox-wrapper">
                        <input
                          type="checkbox"
                          id={module.id}
                          checked={editCompany?.subscribedModules?.includes(
                            String(module.id)
                          )}
                          onChange={() => handleModuleToggle(module.id)}
                        />
                        <label htmlFor={module.id} className="module-label">
                          {module.name}
                        </label>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="submit-wrapper">
                  <button type="submit" className="submit-button">
                    {isLoading ? <LoadingSpinner /> : "Update Client"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </>
    );
  };

  return (
    <section className="w-100 py-2" id="negative-margin">
      {editMode ? renderEditMode() : renderViewMode()}
    </section>
  );
};

export default ClientDetailsPage;
